import Script from "next/script";
import {
  buildStorefrontSnipcartVariables,
  type StorefrontSiteConfig,
} from "@/lib/storefront-site";

const publicApiKey = process.env.NEXT_PUBLIC_SNIPCART_API_KEY;
const snipcartVersion = "3.7.5";
const snipcartAssetVersion = "20260610-02";
const snipcartAssetBaseUrl = `https://cdn.snipcart.com/themes/v${snipcartVersion}/default`;

type SnipcartProps = {
  site: StorefrontSiteConfig;
  customerToken?: string | null;
};

const devErrorFilterScript = `
  (function () {
    if (window.__rlSnipcartDevErrorFilterAttached) {
      return;
    }

    window.__rlSnipcartDevErrorFilterAttached = true;

    function isBenignSnipcartNavigationError(message, stack) {
      var text = String(message || "").toLowerCase();
      var stackText = String(stack || "").toLowerCase();
      var snipcartStack = stackText.indexOf("cdn.snipcart.com/themes/v") !== -1;

      return (
        text.indexOf("bugsnag browser smoke") !== -1 ||
        text.indexOf("bugsnag smoke") !== -1 ||
        text.indexOf('navigation cancelled from "/" to "/checkout" with a new navigation.') !== -1 ||
        (text.indexOf("navigation abort") !== -1 && (text.indexOf("snipcart") !== -1 || snipcartStack)) ||
        (text.indexOf("abort fetching component for route") !== -1 && text.indexOf("checkout") !== -1) ||
        (snipcartStack && text.indexOf("attempted to assign to readonly property.") !== -1) ||
        (snipcartStack && text.indexOf("cannot create property '__na' on string") !== -1)
      );
    }

    window.addEventListener(
      "error",
      function (event) {
        var error = event.error;
        var message = event.message || (error && error.message);
        var stack = event.filename || (error && error.stack);

        if (!isBenignSnipcartNavigationError(message, stack)) {
          return;
        }

        event.preventDefault();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
      },
      true,
    );

    window.addEventListener(
      "unhandledrejection",
      function (event) {
        var reason = event.reason;
        var message = reason && reason.message ? reason.message : reason;
        var stack = reason && reason.stack ? reason.stack : "";

        if (!isBenignSnipcartNavigationError(message, stack)) {
          return;
        }

        event.preventDefault();
      },
      true,
    );
  })();
`;

function buildSnipcartSettings(
  site: StorefrontSiteConfig,
  customerToken?: string | null,
) {
  return JSON.stringify({
    publicApiKey,
    version: snipcartVersion,
    templatesUrl: `/snipcart-templates.html?v=${snipcartAssetVersion}`,
    siteCode: site.code,
    checkoutMode: site.checkoutMode,
    ...(customerToken ? { customerToken } : {}),
  });
}

function buildSnipcartRuntimeScript(site: StorefrontSiteConfig) {
  const variables = buildStorefrontSnipcartVariables(site.code);
  const runtimeConfig = JSON.stringify({
    siteCode: site.code,
    checkoutMode: site.checkoutMode,
    storefrontSiteVersion:
      process.env.NEXT_PUBLIC_STOREFRONT_SITE_VERSION?.trim() ||
      process.env.NEXT_PUBLIC_BUGSNAG_APP_VERSION?.trim() ||
      process.env.NEXT_PUBLIC_APP_VERSION?.trim() ||
      null,
    copy: site.snipcart.copy,
    variables,
  });
  const paymentLogoAssets = JSON.stringify([
    {
      alt: "Visa",
      src: `/payments/visa-light.svg?v=${snipcartAssetVersion}`,
    },
    {
      alt: "Mastercard",
      src: `/payments/mastercard.svg?v=${snipcartAssetVersion}`,
    },
    {
      alt: "Apple Pay",
      src: `/payments/apple-pay-light.svg?v=${snipcartAssetVersion}`,
    },
    {
      alt: "Google Pay",
      src: `/payments/google-pay-light.svg?v=${snipcartAssetVersion}`,
    },
    {
      alt: "PayPal",
      src: `/payments/paypal-light.svg?v=${snipcartAssetVersion}`,
    },
  ]);

  return `
    (function () {
      var runtimeConfig = ${runtimeConfig};
      var paymentLogoAssets = ${paymentLogoAssets};
      var hookRetryTimer = null;
      var rootObserver = null;
      var snipcartModalOpen = false;
      var cartSummaryNoticeText = "Collection and delivery options will be calculated at checkout.";
      var checkoutSummaryNoticeText = "Delivery and collection options will be calculated in the next step.";
      var checkoutStepTitles = {
        "snipcart-checkout-step-billing": "Billing Information",
        "snipcart-checkout-step-shipping": "Delivery or Collection",
        "snipcart-checkout-step-payment": "Payment Information",
      };

      window.__RL_SNIPCART_CONTEXT = runtimeConfig;

      function leaveBreadcrumb(name, metadata) {
        try {
          if (typeof window.__rlBugsnagLeaveBreadcrumb === "function") {
            window.__rlBugsnagLeaveBreadcrumb(name, metadata || {});
          }
        } catch (e) {}
      }

      function getSnipcartRoot() {
        return document.getElementById("snipcart");
      }

      function hashShowsSnipcart() {
        return /^#\\/(cart|checkout|billing|shipping|payment|confirmation|dashboard)/.test(
          String(window.location.hash || ""),
        );
      }

      function syncRootAttributes() {
        var root = getSnipcartRoot();
        if (!root) {
          return null;
        }

        root.setAttribute("data-storefront-site", runtimeConfig.siteCode);
        root.setAttribute("data-storefront-checkout-mode", runtimeConfig.checkoutMode);
        if (runtimeConfig.variables) {
          Object.keys(runtimeConfig.variables).forEach(function (key) {
            root.style.setProperty(key, runtimeConfig.variables[key]);
          });
        }
        return root;
      }

      function syncOpenState() {
        var isOpen = snipcartModalOpen || hashShowsSnipcart();

        if (isOpen) {
          document.documentElement.setAttribute("data-rl-snipcart-open", "true");
          return;
        }

        document.documentElement.removeAttribute("data-rl-snipcart-open");
      }

      function applyCheckoutStepTitles() {
        Object.keys(checkoutStepTitles).forEach(function (stepId) {
          var step = document.getElementById(stepId);
          if (!step) {
            return;
          }

          var title = step.querySelector(
            ".snipcart-billing-completed__title, .snipcart-shipping-completed__title, .snipcart__box--header .snipcart__font--subtitle",
          );

          if (!title) {
            return;
          }

          var desiredTitle = checkoutStepTitles[stepId];
          if (title.textContent !== desiredTitle) {
            title.textContent = desiredTitle;
          }
        });
      }

      function buildPaymentBrandRail() {
        var rail = document.createElement("div");
        rail.className = "rl-featured-payment-rail";

        var title = document.createElement("p");
        title.className = "rl-featured-payment-rail__title";
        title.textContent = "Pay with...";
        rail.appendChild(title);

        var list = document.createElement("ul");
        list.className = "rl-featured-payment-rail__list";
        list.setAttribute("aria-label", "Accepted payment methods");

        paymentLogoAssets.forEach(function (logo) {
          var item = document.createElement("li");
          item.className = "rl-featured-payment-rail__item";

          var image = document.createElement("img");
          image.className = "rl-featured-payment-rail__logo";
          image.src = logo.src;
          image.alt = logo.alt;
          image.decoding = "async";

          item.appendChild(image);
          list.appendChild(item);
        });

        rail.appendChild(list);
        return rail;
      }

      function syncFeaturedPaymentMethods() {
        document
          .querySelectorAll(".snipcart-featured-payment-methods")
          .forEach(function (section) {
            var nativeTitle = section.querySelector(".snipcart-featured-payment-methods__title");
            var nativeLink = section.querySelector(".snipcart-featured-payment-methods__link");
            var nativeList = section.querySelector(".snipcart-featured-payment-methods__list");

            if (nativeTitle) {
              nativeTitle.hidden = true;
            }

            if (nativeLink) {
              nativeLink.hidden = true;
            }

            if (nativeList) {
              nativeList.classList.add("rl-featured-payment-methods__native");
              nativeList.setAttribute("aria-hidden", "true");
            }

            if (!section.querySelector(".rl-featured-payment-rail")) {
              section.appendChild(buildPaymentBrandRail());
            }
          });
      }

      function syncAuthBranding() {
        var inverseLogo = "${site.inverseLogo}?v=${snipcartAssetVersion}";

        /* Ensure any logos rendered by the static template point to the
           correct site-specific inverse logo (the template is shared by
           both storefronts and always references the food logo). */
        document.querySelectorAll("img.rl-snipcart-auth-logo").forEach(function (img) {
          if (img.getAttribute("src") !== inverseLogo) {
            img.setAttribute("src", inverseLogo);
          }
        });

        var authViews = [
          { selector: ".snipcart-forgot-password__title", tagline: "Reset your password" },
        ];

        authViews.forEach(function (view) {
          var title = document.querySelector(view.selector);
          if (!title) {
            return;
          }

          var container = title.closest(".snipcart__box") || title.parentElement;
          if (!container || container.querySelector(".rl-snipcart-auth-header")) {
            return;
          }

          var header = document.createElement("div");
          header.className = "rl-snipcart-auth-header";

          var img = document.createElement("img");
          img.src = inverseLogo;
          img.alt = "Aso Rock";
          img.className = "rl-snipcart-auth-logo";

          var tagline = document.createElement("p");
          tagline.className = "rl-snipcart-auth-tagline";
          tagline.textContent = view.tagline;

          header.appendChild(img);
          header.appendChild(tagline);

          var titleBox = title.closest(".snipcart__box--title");
          if (titleBox && titleBox.nextSibling) {
            container.insertBefore(header, titleBox.nextSibling);
          } else {
            container.insertBefore(header, container.firstChild);
          }
        });
      }

      function syncSummaryNotices() {
        document.querySelectorAll(".snipcart-summary-fees__notice").forEach(function (notice) {
          var desiredText = notice.closest(".snipcart-cart-summary__content")
            ? checkoutSummaryNoticeText
            : cartSummaryNoticeText;

          if (notice.textContent !== desiredText) {
            notice.textContent = desiredText;
          }
        });
      }

      function registerPaymentFormCustomization() {
        if (
          !window.Snipcart ||
          !window.Snipcart.api ||
          !window.Snipcart.api.theme ||
          !window.Snipcart.api.theme.customization ||
          !window.Snipcart.api.theme.customization.registerPaymentFormCustomization
        ) {
          return;
        }

        if (window.__rlSnipcartPaymentCustomizationRegistered) {
          return;
        }

        window.__rlSnipcartPaymentCustomizationRegistered = true;

        window.Snipcart.api.theme.customization.registerPaymentFormCustomization({
          input: {
            backgroundColor: "#ffffff",
            color: "#313332",
            border: "1px solid #000000",
            placeholder: {
              color: "#6b7280",
            },
          },
          label: {
            color: "#313332",
          },
        });
      }

      function syncSnipcartState() {
        syncRootAttributes();
        applyCheckoutStepTitles();
        syncSummaryNotices();
        syncFeaturedPaymentMethods();
        syncAuthBranding();
        syncReorderButtons();
        syncOpenState();
      }

      var manifestDebounceHandle = null;
      var manifestLastHash = null;
      window.__rlReorderManifestInFlight = false;

      function readCartItems() {
        try {
          var state = window.Snipcart && window.Snipcart.store && typeof window.Snipcart.store.getState === "function"
            ? window.Snipcart.store.getState()
            : null;
          var items = state && state.cart && state.cart.items && state.cart.items.items;
          return Array.isArray(items) ? items : [];
        } catch (e) {
          return [];
        }
      }

      function readCartMetadata() {
        try {
          var state = window.Snipcart && window.Snipcart.store && typeof window.Snipcart.store.getState === "function"
            ? window.Snipcart.store.getState()
            : null;
          var metadata = state && state.cart && state.cart.metadata;
          return metadata && typeof metadata === "object" && !Array.isArray(metadata) ? Object.assign({}, metadata) : {};
        } catch (e) {
          return {};
        }
      }

      function buildCartMetadataUpdate(values) {
        var metadata = readCartMetadata();
        metadata.siteCode = runtimeConfig.siteCode;
        // Record the checkout channel on EVERY order so analytics can split
        // web vs in-app revenue. Reuses readAnalyticsSource()'s app-webview
        // detection (pathname /app/checkout, ?source=app-webview, the cart's
        // own metadata.source, or the data-rl-mobile-app-checkout marker) and
        // maps it to the order-metadata vocabulary. Set unconditionally on
        // every write (not spread-and-hope) so a racing writer that re-merges
        // a stale snapshot can never drop it. No server-side default exists by
        // design — a correct value or none, never a guessed one.
        metadata.source = readAnalyticsSource() === "app-webview" ? "mobile-app" : "website";
        // Customer membership — stamp the signed-in Firebase identity onto the
        // cart so the order-completed webhook can link the order to the
        // account. The globals are published by <SnipcartCustomerLink/>.
        if (window.__rlFirebaseUid) {
          metadata.firebaseUid = window.__rlFirebaseUid;
          if (window.__rlFirebaseEmail) {
            metadata.firebaseEmail = window.__rlFirebaseEmail;
          } else {
            delete metadata.firebaseEmail;
          }
        } else {
          delete metadata.firebaseUid;
          delete metadata.firebaseEmail;
        }
        var keys = Object.keys(values || {});
        for (var i = 0; i < keys.length; i++) {
          metadata[keys[i]] = values[keys[i]];
        }
        return metadata;
      }

      function parseMetadataCandidate(value) {
        if (!value) return null;
        if (typeof value === "object") return value;
        if (typeof value !== "string" || !value.trim()) return null;
        try {
          return JSON.parse(value);
        } catch (e) {
          return null;
        }
      }

      function readCustomFieldValue(item, fieldName) {
        var fields = (item && item.customFields) || [];
        var target = String(fieldName || "").trim().toLowerCase();
        for (var i = 0; i < fields.length; i++) {
          var field = fields[i];
          var name = field && field.name ? String(field.name).trim().toLowerCase() : "";
          if (name !== target) continue;
          var value = field.value != null ? field.value : field.displayValue;
          return value != null ? String(value).trim() : null;
        }
        return null;
      }

      function parseItemMetadata(item) {
        if (!item || typeof item !== "object") return null;
        var candidates = [];
        candidates.push(item.metadata);
        candidates.push(readCustomFieldValue(item, "reorderManifest"));
        if (item.dataAttributes && typeof item.dataAttributes === "object") {
          candidates.push(item.dataAttributes["data-item-metadata"] || item.dataAttributes["dataItemMetadata"]);
        }
        if (item.customAttributes && typeof item.customAttributes === "object") {
          candidates.push(item.customAttributes["data-item-metadata"]);
        }
        for (var i = 0; i < candidates.length; i++) {
          var parsed = parseMetadataCandidate(candidates[i]);
          if (parsed && parsed.v === 1 && parsed.productKey) {
            return parsed;
          }
        }
        return null;
      }

      function isOperationalCustomFieldName(name) {
        var normalized = String(name || "").trim().toLowerCase();
        return normalized === "ispot" || normalized === "boxfillunits" || normalized === "reordermanifest";
      }

      function extractCustomFieldValues(item) {
        var values = [];
        var fields = (item && item.customFields) || [];
        for (var i = 0; i < fields.length; i++) {
          var field = fields[i];
          if (!field || !field.name) continue;
          if (isOperationalCustomFieldName(field.name)) continue;
          var rawValue = field.value != null ? field.value : field.displayValue;
          var value = rawValue != null ? String(rawValue).trim() : "";
          if (!value) continue;
          values.push({ name: String(field.name).trim(), value: value });
        }
        return values;
      }

      function normalizeVariantOptionLabel(value) {
        var text = String(value || "").trim();
        if (!text) return "";
        var bracketMatch = text.match(/^(.*)\\[[+-]?\\d+(?:\\.\\d+)?\\]$/);
        if (bracketMatch && bracketMatch[1]) {
          text = bracketMatch[1].trim();
        }
        var displayMatch = text.match(/^(.*)\\s+\\([+-]\\u00A3\\d+(?:\\.\\d+)?\\)$/);
        return displayMatch && displayMatch[1] ? displayMatch[1].trim() : text;
      }

      function resolveSelectionsFromMetadata(meta, customFieldValues) {
        if (!meta || !Array.isArray(meta.groups)) return null;
        var selections = [];
        for (var i = 0; i < customFieldValues.length; i++) {
          var pair = customFieldValues[i];
          var group = null;
          for (var j = 0; j < meta.groups.length; j++) {
            if (meta.groups[j] && meta.groups[j].name === pair.name) {
              group = meta.groups[j];
              break;
            }
          }
          if (!group || !Array.isArray(group.options)) continue;
          var labels = pair.value.split(/\\s*[,|]\\s*/).filter(function (label) {
            return label && label.length > 0;
          });
          for (var k = 0; k < labels.length; k++) {
            var label = labels[k];
            var normalizedLabel = normalizeVariantOptionLabel(label);
            var option = null;
            for (var m = 0; m < group.options.length; m++) {
              if (group.options[m] && normalizeVariantOptionLabel(group.options[m].label) === normalizedLabel) {
                option = group.options[m];
                break;
              }
            }
            if (!option) continue;
            selections.push({
              assignmentKey: group.assignmentKey || group.groupKey,
              optionKey: option.optionKey || option.key
            });
          }
        }
        return selections;
      }

      function buildReorderManifest() {
        var items = readCartItems();
        if (items.length === 0) return null;
        var manifest = [];
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          var meta = parseItemMetadata(item);
          if (!meta || meta.v !== 1 || !meta.productKey) {
            return null;
          }
          var selections = resolveSelectionsFromMetadata(meta, extractCustomFieldValues(item));
          if (selections === null) {
            return null;
          }
          manifest.push({
            productKey: meta.productKey,
            siteCode: meta.siteCode || runtimeConfig.siteCode,
            quantity: item.quantity || 1,
            selectedOptions: selections
          });
        }
        return manifest;
      }

      var analyticsCheckoutSteps = {};
      var lastAnalyticsPurchaseId = null;

      function getSnipcartStoreState() {
        try {
          return window.Snipcart && window.Snipcart.store && typeof window.Snipcart.store.getState === "function"
            ? window.Snipcart.store.getState()
            : null;
        } catch (e) {
          return null;
        }
      }

      function normalizeAnalyticsNumber(value) {
        if (typeof value === "number" && isFinite(value)) return value;
        if (typeof value !== "string") return null;
        var normalized = value.replace(/[^0-9.-]/g, "");
        if (!normalized) return null;
        var parsed = Number(normalized);
        return isFinite(parsed) ? parsed : null;
      }

      function firstAnalyticsNumber() {
        for (var i = 0; i < arguments.length; i++) {
          var value = normalizeAnalyticsNumber(arguments[i]);
          if (value !== null) return value;
        }
        return null;
      }

      function firstAnalyticsText() {
        for (var i = 0; i < arguments.length; i++) {
          if (arguments[i] == null) continue;
          var value = String(arguments[i]).trim();
          if (value) return value;
        }
        return null;
      }

      function cleanAnalyticsObject(value) {
        var result = {};
        Object.keys(value || {}).forEach(function (key) {
          var entry = value[key];
          if (entry === undefined || entry === null || entry === "") return;
          result[key] = entry;
        });
        return result;
      }

      function readAnalyticsSource() {
        var metadata = readCartMetadata();
        var metadataSource = firstAnalyticsText(metadata.source, metadata.checkoutSource);
        if (
          metadataSource === "mobile-app" ||
          metadataSource === "app-webview" ||
          window.location.search.indexOf("source=app-webview") !== -1 ||
          window.location.pathname.indexOf("/app/checkout") === 0 ||
          document.documentElement.getAttribute("data-rl-mobile-app-checkout") === "true"
        ) {
          return "app-webview";
        }
        return "web";
      }

      function buildAnalyticsBasePayload() {
        return cleanAnalyticsObject({
          brand: runtimeConfig.siteCode,
          checkout_mode: runtimeConfig.checkoutMode,
          source: readAnalyticsSource(),
          storefront_site_version: runtimeConfig.storefrontSiteVersion
        });
      }

      function pushAnalyticsEvent(eventName, parameters) {
        try {
          window.dataLayer = window.dataLayer || [];
          var payload = Object.assign(
            { event: eventName },
            buildAnalyticsBasePayload(),
            parameters || {}
          );
          window.dataLayer.push(payload);
          leaveBreadcrumb("analytics.event", {
            eventName: eventName,
            siteCode: runtimeConfig.siteCode,
            source: payload.source
          });
        } catch (e) {}
      }

      function readAnalyticsCurrency(cart) {
        var currency = firstAnalyticsText(
          cart && cart.currency,
          cart && cart.currencyCode,
          cart && cart.currencyIso
        );
        return currency && /^[A-Za-z]{3}$/.test(currency) ? currency.toUpperCase() : "GBP";
      }

      function readCartAnalyticsState() {
        var state = getSnipcartStoreState();
        var cart = state && state.cart ? state.cart : {};
        var rawItems = cart && cart.items && Array.isArray(cart.items.items)
          ? cart.items.items
          : Array.isArray(cart.items)
            ? cart.items
            : readCartItems();
        var items = Array.isArray(rawItems) ? rawItems : [];
        var value = firstAnalyticsNumber(
          cart && cart.finalGrandTotal,
          cart && cart.grandTotal,
          cart && cart.total,
          cart && cart.totalPrice,
          cart && cart.subtotal
        );
        if (value === null) {
          value = 0;
          for (var i = 0; i < items.length; i++) {
            var item = items[i];
            var quantity = firstAnalyticsNumber(item && item.quantity) || 1;
            var price = firstAnalyticsNumber(
              item && item.unitPrice,
              item && item.price,
              item && item.basePrice
            );
            if (price === null) {
              price = firstAnalyticsNumber(item && item.totalPrice, item && item.total);
              if (price !== null && quantity > 0) price = price / quantity;
            }
            if (price !== null) value += price * quantity;
          }
        }
        return {
          cart: cart || {},
          currency: readAnalyticsCurrency(cart),
          items: items,
          value: value
        };
      }

      function buildAnalyticsItem(item) {
        var meta = parseItemMetadata(item) || {};
        var customFields = extractCustomFieldValues(item);
        var variantParts = [];
        for (var i = 0; i < customFields.length; i++) {
          variantParts.push(customFields[i].name + ": " + customFields[i].value);
        }
        var quantity = firstAnalyticsNumber(item && item.quantity) || 1;
        var price = firstAnalyticsNumber(
          item && item.unitPrice,
          item && item.price,
          item && item.basePrice
        );
        if (price === null) {
          var totalPrice = firstAnalyticsNumber(item && item.totalPrice, item && item.total);
          price = totalPrice !== null && quantity > 0 ? totalPrice / quantity : null;
        }
        var category = firstAnalyticsText(
          meta.categoryName,
          meta.category,
          item && item.categoryName,
          item && item.category,
          item && item.categories && item.categories[0]
        );
        return cleanAnalyticsObject({
          item_id: firstAnalyticsText(
            meta.productKey,
            meta.productId,
            item && item.id,
            item && item.uniqueId,
            item && item.token,
            item && item.name
          ),
          item_name: firstAnalyticsText(item && item.name, meta.productName),
          item_category: category,
          item_brand: runtimeConfig.siteCode,
          item_variant: variantParts.length > 0 ? variantParts.join(" | ") : null,
          price: price,
          quantity: quantity
        });
      }

      function buildItemAnalyticsPayload(item) {
        var quantity = firstAnalyticsNumber(item && item.quantity) || 1;
        var price = firstAnalyticsNumber(
          item && item.unitPrice,
          item && item.price,
          item && item.basePrice
        );
        var total = firstAnalyticsNumber(item && item.totalPrice, item && item.total);
        var cartState = readCartAnalyticsState();
        if (total === null && price !== null) total = price * quantity;
        return cleanAnalyticsObject({
          currency: cartState.currency,
          value: total,
          items: [buildAnalyticsItem(item)]
        });
      }

      function buildCartAnalyticsPayload(extra) {
        var cartState = readCartAnalyticsState();
        var items = [];
        for (var i = 0; i < cartState.items.length; i++) {
          items.push(buildAnalyticsItem(cartState.items[i]));
        }
        return Object.assign(
          cleanAnalyticsObject({
            currency: cartState.currency,
            value: cartState.value,
            items: items
          }),
          extra || {}
        );
      }

      function readCheckoutStepRoute() {
        var state = getSnipcartStoreState();
        return [
          state && state.route,
          state && state.context && state.context.route,
          window.location.hash
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
      }

      function readShippingTier(cart) {
        var shippingDetails = cart && cart.shippingDetails ? cart.shippingDetails : {};
        return firstAnalyticsText(
          shippingDetails.method,
          shippingDetails.name,
          shippingDetails.description,
          cart && cart.shippingRateName,
          cart && cart.shippingMethod
        );
      }

      function readPaymentType(cart) {
        return firstAnalyticsText(
          cart && cart.paymentMethod,
          cart && cart.paymentDetails && cart.paymentDetails.method,
          cart && cart.paymentDetails && cart.paymentDetails.cardBrand,
          "snipcart"
        );
      }

      function trackCheckoutStepAnalytics() {
        var route = readCheckoutStepRoute();
        if (!route) return;
        var isCheckout = route.indexOf("checkout") !== -1 ||
          route.indexOf("billing") !== -1 ||
          route.indexOf("shipping") !== -1 ||
          route.indexOf("payment") !== -1;
        if (isCheckout && !analyticsCheckoutSteps.begin_checkout) {
          analyticsCheckoutSteps.begin_checkout = true;
          pushAnalyticsEvent("begin_checkout", buildCartAnalyticsPayload());
        }
        if (route.indexOf("payment") !== -1 && !analyticsCheckoutSteps.add_payment_info) {
          analyticsCheckoutSteps.add_payment_info = true;
          var paymentCartState = readCartAnalyticsState();
          pushAnalyticsEvent(
            "add_payment_info",
            buildCartAnalyticsPayload({ payment_type: readPaymentType(paymentCartState.cart) })
          );
        }
      }

      function buildPurchaseAnalyticsPayload(response) {
        var order = response && (response.cart || response.order || response.data || response);
        var cartState = readCartAnalyticsState();
        var orderItems = order && order.items && Array.isArray(order.items.items)
          ? order.items.items
          : order && Array.isArray(order.items)
            ? order.items
            : cartState.items;
        var items = [];
        for (var i = 0; i < orderItems.length; i++) {
          items.push(buildAnalyticsItem(orderItems[i]));
        }
        var transactionId = firstAnalyticsText(
          order && order.invoiceNumber,
          order && order.number,
          order && order.orderId,
          order && order.id,
          order && order.token,
          cartState.cart && cartState.cart.invoiceNumber,
          cartState.cart && cartState.cart.token
        );
        if (!transactionId) return null;
        return cleanAnalyticsObject({
          transaction_id: transactionId,
          currency: readAnalyticsCurrency(order || cartState.cart),
          value: firstAnalyticsNumber(
            order && order.finalGrandTotal,
            order && order.grandTotal,
            order && order.total,
            order && order.totalPrice,
            cartState.value
          ),
          tax: firstAnalyticsNumber(order && order.taxesTotal, order && order.tax),
          shipping: firstAnalyticsNumber(
            order && order.shippingDetails && order.shippingDetails.cost,
            order && order.shippingRateCost,
            order && order.shipping
          ),
          items: items
        });
      }

      function trackPurchaseAnalytics(response) {
        var payload = buildPurchaseAnalyticsPayload(response);
        if (!payload || !payload.transaction_id) return;
        if (lastAnalyticsPurchaseId === payload.transaction_id) return;
        lastAnalyticsPurchaseId = payload.transaction_id;
        pushAnalyticsEvent("purchase", payload);
      }

      function syncAndTrackSnipcartState() {
        syncSnipcartState();
        trackCheckoutStepAnalytics();
      }

      function hashString(value) {
        var text = String(value || "");
        var hash = 0;
        for (var i = 0; i < text.length; i++) {
          hash = (hash * 31 + text.charCodeAt(i)) | 0;
        }
        return String(hash);
      }

      function scheduleManifestUpdate() {
        if (window.__rlReorderManifestInFlight) return;
        if (manifestDebounceHandle !== null) {
          window.clearTimeout(manifestDebounceHandle);
        }
        manifestDebounceHandle = window.setTimeout(function () {
          manifestDebounceHandle = null;
          writeReorderManifest();
        }, 200);
      }

      // Re-stamp cart metadata on demand (e.g. after the customer signs in or
      // out) so the Firebase identity lands on the cart even when the line
      // items have not changed.
      window.__rlStampCustomerMetadata = function () {
        manifestLastHash = null;
        scheduleManifestUpdate();
      };

      async function writeReorderManifest() {
        if (window.__rlReorderManifestInFlight) return;
        var cartApi = window.Snipcart && window.Snipcart.api && window.Snipcart.api.cart;
        if (!cartApi || typeof cartApi.update !== "function") return;
        var manifest = buildReorderManifest();
        var payload = manifest === null ? null : JSON.stringify(manifest);
        if (payload && payload.length > 1800) {
          payload = null;
        }
        var metadata = buildCartMetadataUpdate({ reorderManifestV1: payload });
        var nextHash = hashString(JSON.stringify(metadata));
        if (nextHash === manifestLastHash) return;
        manifestLastHash = nextHash;
        // Prefill the checkout fields from the saved Firebase customer
        // profile so first-time customers only retype what we don't yet know.
        // Carried on the same cart update as the identity metadata so it
        // re-applies whenever sign-in state changes.
        var customerEmail =
          typeof window.__rlFirebaseEmail === "string"
            ? window.__rlFirebaseEmail.trim()
            : "";
        var customerPhone =
          typeof window.__rlCustomerPhone === "string"
            ? window.__rlCustomerPhone.trim()
            : "";
        var prefillShipping = (typeof window.__rlShippingAddress === "object" && window.__rlShippingAddress !== null)
          ? window.__rlShippingAddress
          : null;
        var prefillBilling = (typeof window.__rlBillingAddress === "object" && window.__rlBillingAddress !== null)
          ? window.__rlBillingAddress
          : null;
        // Phone is the one piece of contact info we always know post-OTP, so
        // make sure it lands on both addresses. If we have no address yet
        // for a side, build a minimal {phone}-only one so Snipcart still
        // prefills the contact phone field on the first-ever checkout.
        if (customerPhone) {
          prefillShipping = prefillShipping
            ? (prefillShipping.phone
                ? prefillShipping
                : Object.assign({}, prefillShipping, { phone: customerPhone }))
            : { phone: customerPhone };
          prefillBilling = prefillBilling
            ? (prefillBilling.phone
                ? prefillBilling
                : Object.assign({}, prefillBilling, { phone: customerPhone }))
            : { phone: customerPhone };
        }
        var cartUpdate = { metadata: metadata };
        if (customerEmail) {
          cartUpdate.email = customerEmail;
        }
        if (prefillShipping) {
          cartUpdate.shippingAddress = prefillShipping;
        }
        if (prefillBilling) {
          cartUpdate.billingAddress = prefillBilling;
          if (prefillShipping) {
            // Honour the separate billing address rather than mirroring the
            // shipping address we just stamped.
            cartUpdate.shippingAddressSameAsBilling = false;
          }
        }
        try {
          await cartApi.update(cartUpdate);
          leaveBreadcrumb("reorder.manifest.write", { itemCount: manifest ? manifest.length : 0, siteCode: runtimeConfig.siteCode });
        } catch (e) {
          manifestLastHash = null;
        }
      }

      function readSnipcartAddress(source) {
        if (!source || typeof source !== "object") return null;
        var address = {
          name: typeof source.name === "string"
            ? source.name
            : (source.fullName || ((source.firstName || "") + " " + (source.lastName || "")).trim()),
          address1: source.address1 || source.address || null,
          address2: source.address2 || null,
          city: source.city || null,
          country: source.country || source.countryCode || null,
          postalCode: source.postalCode || source.zipCode || source.postcode || null,
          phone: source.phone || null
        };
        if (typeof address.name === "string") address.name = address.name.trim();
        for (var key in address) {
          if (typeof address[key] === "string" && !address[key].trim()) {
            address[key] = null;
          }
        }
        var hasContent = false;
        for (var key2 in address) {
          if (address[key2]) { hasContent = true; break; }
        }
        return hasContent ? address : null;
      }

      function pickFirstAddress(/* ...candidates */) {
        for (var i = 0; i < arguments.length; i++) {
          var resolved = readSnipcartAddress(arguments[i]);
          if (resolved) return resolved;
        }
        return null;
      }

      function pickFirstString(/* ...candidates */) {
        for (var i = 0; i < arguments.length; i++) {
          var value = arguments[i];
          if (typeof value === "string") {
            var trimmed = value.trim();
            if (trimmed) return trimmed;
          }
        }
        return null;
      }

      function extractOrderProfile(response) {
        var order = response && (response.cart || response.order || response.data || response);
        var customer = response && response.customer;
        var storeState = null;
        try {
          if (window.Snipcart && window.Snipcart.store && typeof window.Snipcart.store.getState === "function") {
            storeState = window.Snipcart.store.getState();
          }
        } catch (e) {}
        var storeCart = storeState && storeState.cart ? storeState.cart : null;
        var storeCustomer = storeState && storeState.customer && storeState.customer.data
          ? storeState.customer.data
          : null;

        return {
          email: pickFirstString(
            order && order.email,
            customer && customer.email,
            storeCart && storeCart.email,
            storeCustomer && storeCustomer.email
          ),
          shippingAddress: pickFirstAddress(
            order && order.shippingAddress,
            customer && customer.shippingAddress,
            storeCart && storeCart.shippingAddress,
            storeCustomer && storeCustomer.shippingAddress
          ),
          billingAddress: pickFirstAddress(
            order && order.billingAddress,
            customer && customer.billingAddress,
            storeCart && storeCart.billingAddress,
            storeCustomer && storeCustomer.billingAddress
          )
        };
      }

      function captureOrderProfile(response) {
        var profile = extractOrderProfile(response);
        // Log so we can see exactly what landed in the event vs what we
        // extracted — useful when chasing why a Firestore profile didn't
        // get written after a successful order.
        try {
          // eslint-disable-next-line no-console
          console.log("[rl] order.completed payload", response);
          // eslint-disable-next-line no-console
          console.log("[rl] order.completed extracted profile", profile);
        } catch (e) {}
        leaveBreadcrumb("order.profile.capture", {
          hasEmail: !!(profile && profile.email),
          hasShipping: !!(profile && profile.shippingAddress),
          hasBilling: !!(profile && profile.billingAddress),
          captureHookPresent: typeof window.__rlCaptureOrderProfile === "function",
          bridgePresent: !!(window.AsoRockBridge && typeof window.AsoRockBridge.postMessage === "function")
        });
        if (!profile) return;
        // Web path: a signed-in Firebase user; SnipcartCustomerLink wired
        // window.__rlCaptureOrderProfile to a Firestore writer.
        try {
          if (typeof window.__rlCaptureOrderProfile === "function") {
            window.__rlCaptureOrderProfile(profile);
          }
        } catch (e) {
          try { console.error("[rl] __rlCaptureOrderProfile threw", e); } catch (ee) {}
        }
        // App-WebView path: no web Firebase auth here, but the native app is
        // signed in. Forward the same payload back across the bridge so the
        // Flutter side writes to Firestore with its own credentials.
        try {
          if (window.AsoRockBridge && typeof window.AsoRockBridge.postMessage === "function") {
            window.AsoRockBridge.postMessage(JSON.stringify({
              source: "aso-rock-checkout",
              event: "customer-profile-update",
              payload: profile
            }));
          }
        } catch (e) {}
      }

      var cachedAuthToken = { email: null, token: null };
      var mintInFlight = null;

      function readCurrentCustomerEmail() {
        try {
          var state = window.Snipcart && window.Snipcart.store && typeof window.Snipcart.store.getState === "function"
            ? window.Snipcart.store.getState()
            : null;
          var customer = state && (state.customer && state.customer.data ? state.customer.data : state.customer);
          if (!customer || typeof customer.email !== "string") return null;
          var email = customer.email.trim();
          return email || null;
        } catch (e) {
          return null;
        }
      }

      function readCurrentCustomerToken() {
        try {
          var state = window.Snipcart && window.Snipcart.store && typeof window.Snipcart.store.getState === "function"
            ? window.Snipcart.store.getState()
            : null;
          var customer = state && state.customer ? state.customer : null;
          var data = customer && customer.data ? customer.data : null;
          var session = state && state.session ? state.session : null;
          var candidates = [
            customer && customer.customerToken,
            customer && customer.sessionToken,
            customer && customer.token,
            customer && customer.session && customer.session.token,
            data && data.customerToken,
            data && data.sessionToken,
            data && data.token,
            data && data.session && data.session.token,
            session && session.customerToken,
            session && session.token
          ];
          for (var i = 0; i < candidates.length; i++) {
            if (typeof candidates[i] === "string" && candidates[i].trim()) {
              return candidates[i].trim();
            }
          }
        } catch (e) {}
        return null;
      }

      async function persistCustomerSession() {
        var customerToken = readCurrentCustomerToken();
        if (!customerToken) return null;
        try {
          var response = await fetch("/api/app/auth/session", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Storefront-Site": runtimeConfig.siteCode
            },
            body: JSON.stringify({ customerToken: customerToken }),
            cache: "no-store",
            credentials: "same-origin"
          });
          if (!response.ok) return null;
          leaveBreadcrumb("auth.session.persist", { siteCode: runtimeConfig.siteCode });
          return await response.json();
        } catch (e) {
          return null;
        }
      }

      async function clearPersistedCustomerSession() {
        try {
          await fetch("/api/app/auth/session", {
            method: "DELETE",
            headers: {
              Accept: "application/json",
              "X-Storefront-Site": runtimeConfig.siteCode
            },
            cache: "no-store",
            credentials: "same-origin"
          });
          leaveBreadcrumb("auth.session.clear", { siteCode: runtimeConfig.siteCode });
        } catch (e) {}
      }

      async function mintAppAuthToken(email) {
        if (!email) return null;
        if (cachedAuthToken.email === email && cachedAuthToken.token) {
          return cachedAuthToken.token;
        }
        if (mintInFlight) return mintInFlight;
        mintInFlight = (async function () {
          try {
            var response = await fetch("/api/app/auth/mint", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Storefront-Site": runtimeConfig.siteCode
              },
              body: JSON.stringify({ email: email }),
              cache: "no-store",
              credentials: "same-origin"
            });
            if (!response.ok) return null;
            var data = await response.json();
            var token = typeof data.accessToken === "string"
              ? data.accessToken
              : typeof data.token === "string"
                ? data.token
                : null;
            if (token) {
              cachedAuthToken = { email: email, token: token };
              window.__rlAppAuthToken = { email: email, token: token };
            }
            return token;
          } catch (e) {
            return null;
          } finally {
            mintInFlight = null;
          }
        })();
        return mintInFlight;
      }

      function invalidateAuthToken() {
        cachedAuthToken = { email: null, token: null };
        window.__rlAppAuthToken = null;
      }

      var reorderOrderCache = { loadedAt: 0, byToken: {}, byInvoice: {}, items: [], totalItems: 0 };

      async function ensureReorderOrderCacheFresh() {
        var now = Date.now();
        if (now - reorderOrderCache.loadedAt < 60000 && Object.keys(reorderOrderCache.byToken).length > 0) {
          return reorderOrderCache;
        }
        var email = readCurrentCustomerEmail();
        if (!email) return reorderOrderCache;
        var token = await mintAppAuthToken(email);
        if (!token) return reorderOrderCache;
        try {
          var response = await fetch("/api/app/customers/orders?limit=50", {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + token,
              "X-Storefront-Site": runtimeConfig.siteCode
            },
            cache: "no-store",
            credentials: "same-origin"
          });
          if (!response.ok) return reorderOrderCache;
          var body = await response.json();
          leaveBreadcrumb("reorder.orders.cache", { status: response.status, siteCode: runtimeConfig.siteCode });
          var next = { loadedAt: now, byToken: {}, byInvoice: {}, items: [], totalItems: body && body.totalItems ? body.totalItems : 0 };
          var items = body && Array.isArray(body.items) ? body.items : [];
          for (var i = 0; i < items.length; i++) {
            var item = items[i];
            if (!item || !item.token) continue;
            next.byToken[item.token] = {
              canReorder: !!item.canReorder,
              reason: item.reorderUnavailableReason || null,
              invoiceNumber: item.invoiceNumber || null,
              placedOn: item.placedOn || null,
              total: item.total,
              currency: item.currency || null,
              primaryItemName: item.primaryItemName || null
            };
            if (item.invoiceNumber) {
              next.byInvoice[String(item.invoiceNumber).trim().toUpperCase()] = item.token;
            }
            next.items.push(item);
          }
          reorderOrderCache = next;
        } catch (e) { /* fallback to stale cache */ }
        return reorderOrderCache;
      }

      function extractOrderTokenFromText(value) {
        var text = String(value || "");
        var patterns = [
          /\\/invoice\\/([A-Za-z0-9_-]+)/i,
          /#\\/(?:invoice|order|orders)\\/([A-Za-z0-9_-]+)/i,
          /#\\/dashboard\\/(?:invoice|order|orders)\\/([A-Za-z0-9_-]+)/i,
          /(?:invoice|order|token)=([A-Za-z0-9_-]+)/i
        ];
        for (var i = 0; i < patterns.length; i++) {
          var match = text.match(patterns[i]);
          if (match && match[1]) return match[1];
        }
        return null;
      }

      function extractOrderTokenFromElement(element) {
        if (!element) return null;
        var href = element.getAttribute && (element.getAttribute("href") || element.getAttribute("data-href"));
        var token = extractOrderTokenFromText(href || element.href || "");
        if (token) return token;
        if (element.dataset) {
          var datasetKeys = Object.keys(element.dataset);
          for (var i = 0; i < datasetKeys.length; i++) {
            token = extractOrderTokenFromText(element.dataset[datasetKeys[i]]);
            if (token) return token;
            if (/token|order/i.test(datasetKeys[i]) && element.dataset[datasetKeys[i]]) {
              return element.dataset[datasetKeys[i]];
            }
          }
        }
        var parent = element.closest && element.closest("[data-token], [data-order-token], [data-order]");
        if (parent && parent !== element) return extractOrderTokenFromElement(parent);
        return null;
      }

      function extractInvoiceNumberFromCard(element) {
        if (!element) return null;
        var text = String(element.textContent || "");
        var match = text.match(/\b(?:ROCK|ASO|SNIP)-\d+\b/i);
        return match && match[0] ? match[0].toUpperCase() : null;
      }

      function findCachedTokenByInvoiceNumber(invoiceNumber) {
        if (!invoiceNumber || !reorderOrderCache.items || !reorderOrderCache.items.length) {
          return null;
        }
        var cached = reorderOrderCache.byInvoice[String(invoiceNumber).trim().toUpperCase()];
        if (cached) return cached;
        for (var i = 0; i < reorderOrderCache.items.length; i++) {
          var item = reorderOrderCache.items[i];
          if (!item || !item.token || !item.invoiceNumber) continue;
          if (String(item.invoiceNumber).trim().toUpperCase() === String(invoiceNumber).trim().toUpperCase()) {
            return item.token;
          }
        }
        return null;
      }

      function extractOrderTokenFromLink(link) {
        return extractOrderTokenFromElement(link);
      }

      function getDashboardRoot() {
        var root = getSnipcartRoot();
        if (!root) return null;
        return root.querySelector(".snipcart-dashboard, .snipcart-tab, [class*='dashboard'], [data-testid*='dashboard']")
          || root.querySelector(".snipcart-layout__content, .snipcart-modal__container")
          || root;
      }

      var reorderPanelReason = null;
      var reorderDashboardWaitMs = 2200;
      var reorderDashboardVisibilityStartedAt = 0;
      var reorderDashboardVisibilityTimer = null;

      function getOrderCardElements(scope) {
        var root = scope || getDashboardRoot() || getSnipcartRoot();
        if (!root || typeof root.querySelectorAll !== "function") return [];
        return Array.prototype.slice.call(root.querySelectorAll(
          ".snipcart-order, .snipcart-order__card, [class*='order-card'], [class*='order__content'], [data-order], [data-order-token]"
        ));
      }

      function resolveOrderTokenForCard(orderCard, anchor) {
        if (!orderCard) return null;
        return extractOrderTokenFromElement(orderCard)
          || extractOrderTokenFromElement(anchor)
          || findCachedTokenByInvoiceNumber(extractInvoiceNumberFromCard(orderCard));
      }

      function shouldHideUnresolvedDashboardOrders() {
        if (!hashShowsDashboard() || !readCurrentCustomerEmail()) return false;
        if (!reorderOrderCache.loadedAt) return false;
        if (reorderDashboardVisibilityStartedAt === 0) return false;
        return Date.now() - reorderDashboardVisibilityStartedAt >= reorderDashboardWaitMs;
      }

      function syncDashboardOrderVisibility(scope) {
        var root = scope || getDashboardRoot() || getSnipcartRoot();
        if (!root || !hashShowsDashboard() || !readCurrentCustomerEmail() || !reorderOrderCache.loadedAt) {
          reorderDashboardVisibilityStartedAt = 0;
          if (reorderDashboardVisibilityTimer !== null) {
            window.clearTimeout(reorderDashboardVisibilityTimer);
            reorderDashboardVisibilityTimer = null;
          }
          return;
        }

        if (reorderDashboardVisibilityStartedAt === 0) {
          reorderDashboardVisibilityStartedAt = Date.now();
        }

        var hideUnresolved = shouldHideUnresolvedDashboardOrders();
        if (!hideUnresolved && reorderDashboardVisibilityTimer === null) {
          reorderDashboardVisibilityTimer = window.setTimeout(function () {
            reorderDashboardVisibilityTimer = null;
            syncSnipcartState();
          }, Math.max(100, reorderDashboardWaitMs - (Date.now() - reorderDashboardVisibilityStartedAt)));
        }
        var cards = getOrderCardElements(root);
        for (var i = 0; i < cards.length; i++) {
          var card = cards[i];
          var anchorInfo = findReorderAnchor(card);
          var token = resolveOrderTokenForCard(card, anchorInfo && anchorInfo.link);
          var belongsToCurrentSite = !!(token && reorderOrderCache.byToken[token]);
          if (belongsToCurrentSite) {
            card.classList.remove("rl-snipcart-order-hidden");
            card.removeAttribute("aria-hidden");
            continue;
          }

          if (token || hideUnresolved) {
            card.classList.add("rl-snipcart-order-hidden");
            card.setAttribute("aria-hidden", "true");
          }
        }
      }

      function findReorderAnchor(orderEl) {
        if (!orderEl || typeof orderEl.querySelectorAll !== "function") return null;
        var actionLink = orderEl.querySelector(
          "a[href*='/invoice/'], a[href*='#/invoice'], a[href*='#/order'], a[href*='invoice'], a[href*='order']"
        );
        if (!actionLink) {
          var actionCandidates = orderEl.querySelectorAll("a[href], button, [role='button']");
          for (var i = 0; i < actionCandidates.length; i++) {
            if (isReorderActionCandidate(actionCandidates[i])) {
              actionLink = actionCandidates[i];
              break;
            }
          }
        }
        if (!actionLink) return null;
        var actionContainer = actionLink.closest(
          ".snipcart-order__actions, .snipcart-order__header, .snipcart-order__header-item, .snipcart__actions, [class*='actions'], [class*='header']"
        ) || actionLink.parentElement || orderEl;
        return { container: actionContainer, link: actionLink };
      }

      function isReorderActionCandidate(element) {
        if (!element || element.classList && element.classList.contains("rl-snipcart-reorder-button")) return false;
        var href = element.getAttribute && (element.getAttribute("href") || element.getAttribute("data-href") || "");
        var text = (element.textContent || "").trim();
        if (/invoice|receipt|view order|order details/i.test(text)) return true;
        if (/invoice|order/i.test(String(href))) return true;
        return !!extractOrderTokenFromElement(element);
      }

      async function syncReorderButtons() {
        var root = getSnipcartRoot();
        var dashboardRoot = getDashboardRoot();
        if (hashShowsDashboard() && readCurrentCustomerEmail()) {
          await ensureReorderOrderCacheFresh();
          syncDashboardOrderVisibility(dashboardRoot || root);
        }
        await syncReorderPanel();
      }

      var fallbackPanelInFlight = false;

      function hashShowsDashboard() {
        return /#\\/dashboard/i.test(String(window.location.hash || ""));
      }

      function removeReorderPanel() {
        var existing = document.querySelector(".rl-snipcart-reorder-panel");
        if (existing) existing.remove();
      }

      function findReorderPanelAnchor(root) {
        if (!root || typeof root.querySelector !== "function") return null;
        var customerDetails = root.querySelector(".snipcart-customer-details");
        if (customerDetails && customerDetails.parentElement) {
          return { mode: "after", element: customerDetails };
        }
        var sidebar = root.querySelector(".snipcart-layout-sidebar__main, .snipcart-layout-sidebar__aside");
        if (sidebar) {
          return { mode: "append", element: sidebar };
        }
        return { mode: "append", element: root };
      }

      function panelIsAnchoredCorrectly(panel, anchor) {
        if (!panel || !anchor || !anchor.element) return false;
        if (anchor.mode === "after") {
          return panel.previousElementSibling === anchor.element
            && panel.parentElement === anchor.element.parentElement;
        }
        return panel.parentElement === anchor.element;
      }

      function attachReorderPanel(panel, anchor) {
        if (!anchor || !anchor.element) return;
        if (anchor.mode === "after") {
          var parent = anchor.element.parentElement;
          if (!parent) return;
          parent.insertBefore(panel, anchor.element.nextSibling);
        } else {
          anchor.element.appendChild(panel);
        }
      }

      async function syncReorderPanel() {
        if (fallbackPanelInFlight) return;
        if (!hashShowsDashboard()) {
          reorderPanelReason = null;
          removeReorderPanel();
          return;
        }
        var root = getDashboardRoot();
        if (!root || !readCurrentCustomerEmail()) {
          removeReorderPanel();
          return;
        }
        fallbackPanelInFlight = true;
        try {
          var cache = await ensureReorderOrderCacheFresh();
          if (!cache.items || cache.items.length === 0) {
            removeReorderPanel();
            return;
          }
          var anchor = findReorderPanelAnchor(root);
          var panel = document.querySelector(".rl-snipcart-reorder-panel");
          if (panel && !panelIsAnchoredCorrectly(panel, anchor)) {
            panel.remove();
            panel = null;
          }
          if (!panel) {
            panel = document.createElement("section");
            panel.className = "rl-snipcart-reorder-panel snipcart__box";
            panel.setAttribute("aria-label", "Reorder past orders");
            attachReorderPanel(panel, anchor);
          }
          renderReorderPanel(panel, cache.items);
          reorderPanelReason = anchor ? anchor.mode : null;
          leaveBreadcrumb("reorder.dashboard.panel", {
            orderCount: cache.items.length,
            siteCode: runtimeConfig.siteCode,
            anchor: reorderPanelReason
          });
        } finally {
          fallbackPanelInFlight = false;
        }
      }

      function renderReorderPanel(panel, items) {
        panel.innerHTML = "";
        var title = document.createElement("h3");
        title.className = "rl-snipcart-reorder-panel__title";
        title.textContent = "Order Again";
        panel.appendChild(title);
        var list = document.createElement("div");
        list.className = "rl-snipcart-reorder-panel__list";
        panel.appendChild(list);
        var eligibleItems = [];
        for (var x = 0; x < items.length; x++) {
          if (items[x] && items[x].token && items[x].canReorder) {
            eligibleItems.push(items[x]);
          }
        }
        eligibleItems = eligibleItems.slice(0, 3);
        for (var i = 0; i < eligibleItems.length; i++) {
          var item = eligibleItems[i];
          if (!item || !item.token) continue;
          var row = document.createElement("div");
          row.className = "rl-snipcart-reorder-panel__row";
          var copy = document.createElement("div");
          copy.className = "rl-snipcart-reorder-panel__copy";
          var label = document.createElement("span");
          label.className = "rl-snipcart-reorder-panel__label";
          label.textContent = item.invoiceNumber || "Past order";
          var meta = document.createElement("span");
          meta.className = "rl-snipcart-reorder-panel__meta";
          meta.textContent = item.primaryItemName || (item.itemCount ? item.itemCount + " items" : "Order details");
          copy.appendChild(label);
          copy.appendChild(meta);
          var button = document.createElement("button");
          button.type = "button";
          button.className = "rl-snipcart-reorder-button";
          button.setAttribute("data-reorder-token", item.token);
          button.textContent = "Order Again";
          button.addEventListener("click", function (event) {
            event.preventDefault();
            var current = event.currentTarget;
            handleReorderClick(current, current.getAttribute("data-reorder-token"));
          });
          row.appendChild(copy);
          row.appendChild(button);
          list.appendChild(row);
        }
        if (eligibleItems.length === 0) {
          removeReorderPanel();
        }
      }

      function reorderUnavailableTooltip(reason) {
        switch (String(reason || "")) {
          case "product_not_found":
            return "One or more items are no longer on the menu.";
          case "variant_option_invalid":
          case "variant_required":
            return "This order's options aren't available on the current menu.";
          case "site_mismatch":
            return "This order was placed on a different Aso Rock store.";
          case "no_manifest":
          case "manifest_invalid":
            return "Reorder isn't available for older orders.";
          default:
            return "Reorder isn't available for this order.";
        }
      }

      async function handleReorderClick(button, orderToken) {
        if (!orderToken || button.disabled) return;
        var cartItems = readCartItems();
        if (cartItems.length > 0) {
          var confirmed = await showReorderConfirmDialog();
          if (!confirmed) return;
        }
        var email = readCurrentCustomerEmail();
        if (!email) return;
        var token = await mintAppAuthToken(email);
        if (!token) return;
        var originalLabel = button.textContent;
        button.disabled = true;
        button.classList.add("rl-snipcart-reorder-button--loading");
        button.textContent = "Preparing...";
        try {
          var response = await fetch("/api/app/customers/orders/" + encodeURIComponent(orderToken) + "/reorder", {
            method: "POST",
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + token,
              "X-Storefront-Site": runtimeConfig.siteCode
            },
            cache: "no-store",
            credentials: "same-origin"
          });
          if (!response.ok) {
            if (response.status === 401) invalidateAuthToken();
            throw new Error("reorder_failed");
          }
          var body = await response.json();
          leaveBreadcrumb("reorder.api.response", {
            canReorder: !!(body && body.canReorder),
            reason: body && body.reason,
            lineCount: body && Array.isArray(body.lines) ? body.lines.length : 0,
            siteCode: runtimeConfig.siteCode
          });
          if (!body || !body.canReorder || !Array.isArray(body.lines) || body.lines.length === 0) {
            button.classList.add("rl-snipcart-reorder-button--unavailable");
            button.textContent = "Unavailable";
            button.title = reorderUnavailableTooltip(body && body.reason);
            return;
          }
          await applyReorderLines(body.lines);
          await openCartAfterReorder();
        } catch (e) {
          button.classList.remove("rl-snipcart-reorder-button--loading");
          button.disabled = false;
          button.textContent = originalLabel;
        }
      }

      function synthesiseOptionsText(options) {
        if (!Array.isArray(options) || options.length === 0) return "";
        var parts = [];
        for (var i = 0; i < options.length; i++) {
          var option = options[i];
          if (!option) continue;
          var label = option.label != null ? String(option.label) : "";
          if (!label) continue;
          var modifier = Number(option.priceModifier);
          if (modifier && !isNaN(modifier)) {
            var sign = modifier >= 0 ? "+" : "-";
            var displayLabel = normalizeVariantOptionLabel(label) === label
              ? label + " (" + sign + "\\u00A3" + Math.abs(modifier).toFixed(2) + ")"
              : label;
            parts.push(displayLabel + "[" + sign + Math.abs(modifier).toFixed(2) + "]");
          } else {
            parts.push(label);
          }
        }
        return parts.join("|");
      }

      // Snipcart's cart.items.add API requires customFields[].options as a pipe-delimited
      // string (e.g. "Small|Medium[+1.00]|Large[+2.00]") for the in-cart dropdown to render.
      // Passing an array of objects silently disables the dropdown. The Umbraco endpoint
      // returns customFields from the *current live product* so the option list reflects
      // today's menu and Snipcart's crawler validates against the product page.
      function buildReorderProductDefinition(line) {
        if (!line || !line.product) return null;
        var product = line.product;
        var attrs = product.buttonAttributes || {};
        return {
          id: product.itemId,
          name: product.name,
          price: product.price,
          url: product.url,
          description: product.description,
          image: product.imageUrl || undefined,
          quantity: line.quantity || 1,
          stackable: attrs["data-item-stackable"] || undefined,
          metadata: attrs["data-item-metadata"] || undefined,
          customFields: (product.customFields || []).map(function (field) {
            var optionsText = field.optionsText || synthesiseOptionsText(field.options);
            return {
              name: field.name,
              type: field.type,
              required: field.required,
              value: field.value || undefined,
              options: optionsText || undefined
            };
          })
        };
      }

      async function applyReorderLines(lines) {
        var itemsApi = window.Snipcart && window.Snipcart.api && window.Snipcart.api.cart && window.Snipcart.api.cart.items;
        if (!itemsApi) throw new Error("cart_items_api_unavailable");
        window.__rlReorderManifestInFlight = true;
        try {
          var products = [];
          for (var i = 0; i < lines.length; i++) {
            var def = buildReorderProductDefinition(lines[i]);
            if (def) products.push(def);
          }
          if (typeof itemsApi.replace === "function") {
            await itemsApi.replace.apply(itemsApi, products);
          } else {
            var current = readCartItems();
            for (var j = 0; j < current.length; j++) {
              var uniqueId = current[j] && current[j].uniqueId;
              if (uniqueId) {
                await itemsApi.remove(uniqueId);
              }
            }
            for (var k = 0; k < products.length; k++) {
              await itemsApi.add(products[k]);
            }
          }
          leaveBreadcrumb("reorder.cart.replace", { lineCount: products.length, siteCode: runtimeConfig.siteCode });
        } finally {
          window.__rlReorderManifestInFlight = false;
          manifestLastHash = null;
          scheduleManifestUpdate();
        }
      }

      async function openCartAfterReorder() {
        var api = window.Snipcart && window.Snipcart.api && window.Snipcart.api.theme && window.Snipcart.api.theme.cart;
        if (api && typeof api.open === "function") {
          try { await api.open(); } catch (e) { /* swallow */ }
        }
      }

      var reorderConfirmDialogId = 0;

      function showReorderConfirmDialog() {
        return new Promise(function (resolve) {
          var existing = document.querySelector(".rl-snipcart-reorder-confirm");
          if (existing) existing.remove();

          var dialogId = ++reorderConfirmDialogId;
          var titleId = "rl-snipcart-reorder-confirm-title-" + dialogId;
          var bodyId = "rl-snipcart-reorder-confirm-body-" + dialogId;
          var previousFocus = document.activeElement;

          var overlay = document.createElement("div");
          overlay.className = "rl-snipcart-reorder-confirm";
          overlay.setAttribute("role", "dialog");
          overlay.setAttribute("aria-modal", "true");
          overlay.setAttribute("aria-labelledby", titleId);
          overlay.setAttribute("aria-describedby", bodyId);

          var box = document.createElement("div");
          box.className = "rl-snipcart-reorder-confirm__box";

          var title = document.createElement("h3");
          title.className = "rl-snipcart-reorder-confirm__title";
          title.id = titleId;
          title.textContent = "Replace your cart?";

          var body = document.createElement("p");
          body.className = "rl-snipcart-reorder-confirm__body";
          body.id = bodyId;
          body.textContent = "Your cart already has items. Reordering will replace the current cart contents.";

          var actions = document.createElement("div");
          actions.className = "rl-snipcart-reorder-confirm__actions";

          var cancel = document.createElement("button");
          cancel.type = "button";
          cancel.className = "rl-snipcart-reorder-confirm__cancel";
          cancel.textContent = "Keep current cart";

          var confirm = document.createElement("button");
          confirm.type = "button";
          confirm.className = "rl-snipcart-reorder-confirm__confirm";
          confirm.textContent = "Replace and order again";

          function close(result) {
            document.removeEventListener("keydown", onKeyDown, true);
            overlay.remove();
            try {
              if (previousFocus && typeof previousFocus.focus === "function") {
                previousFocus.focus();
              }
            } catch (e) { /* swallow */ }
            resolve(result);
          }

          function onKeyDown(event) {
            if (event.key === "Escape") {
              event.preventDefault();
              close(false);
              return;
            }
            if (event.key !== "Tab") return;
            var focusable = [cancel, confirm];
            var current = document.activeElement;
            var index = focusable.indexOf(current);
            if (index === -1) {
              event.preventDefault();
              focusable[event.shiftKey ? focusable.length - 1 : 0].focus();
              return;
            }
            var nextIndex = event.shiftKey ? index - 1 : index + 1;
            if (nextIndex < 0 || nextIndex >= focusable.length) {
              event.preventDefault();
              focusable[nextIndex < 0 ? focusable.length - 1 : 0].focus();
            }
          }

          cancel.addEventListener("click", function () { close(false); });
          confirm.addEventListener("click", function () { close(true); });
          overlay.addEventListener("click", function (event) {
            if (event.target === overlay) close(false);
          });
          document.addEventListener("keydown", onKeyDown, true);

          actions.appendChild(cancel);
          actions.appendChild(confirm);
          box.appendChild(title);
          box.appendChild(body);
          box.appendChild(actions);
          overlay.appendChild(box);
          document.body.appendChild(overlay);
          confirm.focus();
        });
      }

      function observeRoot() {
        var root = getSnipcartRoot();
        if (!root) {
          return;
        }

        if (rootObserver) {
          rootObserver.disconnect();
        }

        rootObserver = new MutationObserver(function () {
          syncSnipcartState();
        });

        rootObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["class", "hidden", "style", "aria-hidden"],
        });
      }

      function attachSnipcartHooks() {
        if (!window.Snipcart || !window.Snipcart.events || !window.Snipcart.events.on) {
          if (hookRetryTimer !== null) {
            return;
          }

          hookRetryTimer = window.setTimeout(function () {
            hookRetryTimer = null;
            attachSnipcartHooks();
          }, 200);
          return;
        }

        if (window.__rlSnipcartStateHooksAttached) {
          return;
        }

        window.__rlSnipcartStateHooksAttached = true;
        registerPaymentFormCustomization();

        window.Snipcart.events.on("cart.opened", function () {
          snipcartModalOpen = true;
          syncSnipcartState();
          scheduleManifestUpdate();
          pushAnalyticsEvent("view_cart", buildCartAnalyticsPayload());
          trackCheckoutStepAnalytics();
          var email = readCurrentCustomerEmail();
          if (email) {
            mintAppAuthToken(email);
          }
        });
        window.Snipcart.events.on("cart.closed", function () {
          snipcartModalOpen = false;
          syncSnipcartState();
        });
        window.Snipcart.events.on("item.added", function (item) {
          scheduleManifestUpdate();
          pushAnalyticsEvent("add_to_cart", buildItemAnalyticsPayload(item));
        });
        window.Snipcart.events.on("item.updated", scheduleManifestUpdate);
        window.Snipcart.events.on("item.removed", function (item) {
          scheduleManifestUpdate();
          pushAnalyticsEvent("remove_from_cart", buildItemAnalyticsPayload(item));
        });
        window.Snipcart.events.on("customer.signedin", function (customer) {
          var email = customer && typeof customer.email === "string" ? customer.email.trim() : null;
          if (email) mintAppAuthToken(email);
          persistCustomerSession();
          pushAnalyticsEvent("login", { method: "snipcart" });
        });
        window.Snipcart.events.on("customer.registered", function (customer) {
          var email = customer && typeof customer.email === "string" ? customer.email.trim() : null;
          if (email) mintAppAuthToken(email);
          persistCustomerSession();
          pushAnalyticsEvent("sign_up", { method: "snipcart" });
        });
        window.Snipcart.events.on("customer.signedout", function () {
          invalidateAuthToken();
          clearPersistedCustomerSession();
        });
        window.Snipcart.events.on("cart.confirmed", trackPurchaseAnalytics);
        window.Snipcart.events.on("order.completed", function (response) {
          trackPurchaseAnalytics(response);
          captureOrderProfile(response);
        });
        window.Snipcart.events.on("page.changed", syncAndTrackSnipcartState);
        window.Snipcart.events.on("theme.routechanged", syncAndTrackSnipcartState);
        window.Snipcart.events.on("item.adding", function (item) {
          pushAnalyticsEvent("add_to_cart_attempted", {
            item_id: firstAnalyticsText(item && item.id, item && item.uniqueId, item && item.name),
            item_name: firstAnalyticsText(item && item.name)
          });
        });
        window.Snipcart.events.on("shipping.selected", function (rate) {
          pushAnalyticsEvent("add_shipping_info", buildCartAnalyticsPayload({
            shipping_tier: firstAnalyticsText(rate && rate.name, rate && rate.description, rate && rate.id),
            shipping: firstAnalyticsNumber(rate && rate.cost, rate && rate.rate)
          }));
        });
        window.Snipcart.events.on("discount.applied", function (coupon) {
          pushAnalyticsEvent("apply_promo_code", {
            coupon: firstAnalyticsText(coupon && coupon.code, coupon && coupon.name),
            discount: firstAnalyticsNumber(coupon && coupon.amount, coupon && coupon.value)
          });
        });
        window.Snipcart.events.on("address.changed", function (address) {
          pushAnalyticsEvent("address_confirmed", {
            shipping_country: firstAnalyticsText(address && address.country, address && address.countryCode)
          });
        });
        window.Snipcart.events.on("payment.failed", function (error) {
          pushAnalyticsEvent("payment_error", {
            error_message: firstAnalyticsText(error && error.message, error && error.type, "unknown")
          });
        });
      }

      window.__RLSyncSnipcartState = syncSnipcartState;

      window.__rlReorderDebug = function () {
        var state = window.Snipcart && window.Snipcart.store && typeof window.Snipcart.store.getState === "function"
          ? window.Snipcart.store.getState()
          : null;
        var cart = state && state.cart ? state.cart : {};
        var root = getSnipcartRoot();
        var dashboardRoot = getDashboardRoot();
        var dashboardRootCandidates = root && root.querySelectorAll
          ? root.querySelectorAll(".snipcart-dashboard, .snipcart-tab, [class*='dashboard'], [data-testid*='dashboard']")
          : [];
        var orderEls = root && root.querySelectorAll
          ? root.querySelectorAll(".snipcart-order, .snipcart-order__card, [class*='order']")
          : [];
        var anchors = [];
        for (var i = 0; i < orderEls.length; i++) {
          anchors.push(!!findReorderAnchor(orderEls[i]));
        }
        var sampleItem = (cart.items && cart.items.items && cart.items.items[0]) || null;
        var panelEl = document.querySelector(".rl-snipcart-reorder-panel");
        return {
          scriptVersion: "${snipcartAssetVersion}",
          hash: String(window.location.hash || ""),
          snipcartRoute: state && state.route ? state.route : null,
          dashboardRootFound: !!dashboardRoot,
          dashboardRootCandidateCount: dashboardRootCandidates.length,
          cartItemCount: (cart.items && cart.items.items) ? cart.items.items.length : 0,
          sampleItemKeys: sampleItem ? Object.keys(sampleItem) : [],
          sampleItemMetadata: sampleItem ? sampleItem.metadata : null,
          sampleItemDataAttributes: sampleItem ? sampleItem.dataAttributes : null,
          sampleItemCustomAttributes: sampleItem ? sampleItem.customAttributes : null,
          sampleItemCustomFields: sampleItem ? sampleItem.customFields : null,
          cartMetadata: cart.metadata || null,
          builtManifest: buildReorderManifest(),
          injectorOrders: orderEls.length,
          hiddenOrderCardCount: document.querySelectorAll(".rl-snipcart-order-hidden").length,
          panelMounted: !!panelEl,
          panelParent: panelEl && panelEl.parentElement ? panelEl.parentElement.className : null,
          panelEligible: hashShowsDashboard() && !!dashboardRoot,
          panelReason: reorderPanelReason,
          orderCardCount: orderEls.length,
          visibilityWaitStartedAt: reorderDashboardVisibilityStartedAt || null,
          orderCacheLoadedAt: reorderOrderCache.loadedAt,
          orderCacheCount: reorderOrderCache.items ? reorderOrderCache.items.length : 0,
          orderCacheInvoiceCount: reorderOrderCache.byInvoice ? Object.keys(reorderOrderCache.byInvoice).length : 0,
          injectorAnchors: anchors,
          customerEmail: readCurrentCustomerEmail(),
          customerTokenFound: !!readCurrentCustomerToken(),
          authTokenCached: !!(window.__rlAppAuthToken && window.__rlAppAuthToken.token)
        };
      };

      document.addEventListener("DOMContentLoaded", function () {
        syncSnipcartState();
        observeRoot();
      });
      window.addEventListener("load", syncSnipcartState);
      window.addEventListener("hashchange", syncSnipcartState);
      window.addEventListener("pageshow", syncSnipcartState);
      document.addEventListener("snipcart.ready", function () {
        attachSnipcartHooks();
        observeRoot();
        syncSnipcartState();
      });
    })();
  `;
}

export function Snipcart({ site, customerToken }: SnipcartProps) {
  if (!publicApiKey) {
    return null;
  }

  return (
    <>
      <link rel="stylesheet" href={`${snipcartAssetBaseUrl}/snipcart.css`} />
      <link
        rel="stylesheet"
        href={`/snipcart-overrides.css?v=${snipcartAssetVersion}`}
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.SnipcartSettings = Object.assign({}, window.SnipcartSettings, ${buildSnipcartSettings(site, customerToken)});`,
        }}
      />
      <script
        dangerouslySetInnerHTML={{
          __html: buildSnipcartRuntimeScript(site),
        }}
      />
      {process.env.NODE_ENV !== "production" ? (
        <script
          dangerouslySetInnerHTML={{
            __html: devErrorFilterScript,
          }}
        />
      ) : null}
      <Script
        id="snipcart-script"
        src={`${snipcartAssetBaseUrl}/snipcart.js`}
        strategy="afterInteractive"
      />
      <Script
        id="snipcart-postcode-lookup"
        src={`/snipcart-postcode-lookup.js?v=${snipcartAssetVersion}`}
        strategy="afterInteractive"
      />
      <Script
        id="snipcart-fulfilment-toggle"
        src={`/snipcart-fulfilment-toggle.js?v=${snipcartAssetVersion}`}
        strategy="afterInteractive"
      />
      <Script
        id="snipcart-phone-validation"
        src={`/snipcart-phone-validation.js?v=${snipcartAssetVersion}`}
        strategy="afterInteractive"
      />
      <div
        id="snipcart"
        hidden
        data-api-key={publicApiKey}
        data-storefront-site={site.code}
        data-storefront-checkout-mode={site.checkoutMode}
        style={buildStorefrontSnipcartVariables(site.code)}
        suppressHydrationWarning
      />
    </>
  );
}
